'use client'

import { useTranslations } from 'next-intl'
import { ComponentProps } from 'react'

import { StickyContainer } from '@/shared/lib'
import { Sheet, type SheetRootProps } from '@/shared/ui'

import { Header } from './header'
import { ImageList } from './image-list'
import { useLightbox } from '../../lib/lightbox'
import { Lightbox } from '../lightbox'

type Props = Pick<SheetRootProps, 'open' | 'onOpenChange'> & {
  onClose: () => void
  roomId: number
  like: boolean
  images: string[]
  roomName: string
  selectedIndex: number
  showLikeButton?: boolean
  roomUrl: string | null
  onShared?: () => void
}

export const ImageGallery = ({
  open,
  onClose,
  roomId,
  like,
  images,
  roomName,
  selectedIndex,
  showLikeButton = true,
  roomUrl,
  onShared,
  ...restProps
}: Props) => {
  const t = useTranslations('room_detail')

  const {
    lightboxIndex,
    open: openLightbox,
    close: closeLightbox,
  } = useLightbox()

  const handleLightboxClose = () => {
    closeLightbox()
  }

  const handleImageClick = (index: number) => {
    openLightbox(index)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose?.()
    }
  }

  const handleInteractOutside: ComponentProps<
    typeof Sheet.Content
  >['onInteractOutside'] = event => {
    event.preventDefault()
  }

  return (
    <Sheet.Root
      open={open}
      onOpenChange={handleOpenChange}
      modal={false}
      {...restProps}
    >
      <Sheet.Content
        side="bottom"
        className="z-sheet h-[calc(100vh-72px)] overflow-scroll border-none"
        onInteractOutside={handleInteractOutside}
      >
        <Sheet.Description className="sr-only">
          {roomName} {t('image_gallery')}
        </Sheet.Description>
        <div className="mx-auto w-[700px]">
          <StickyContainer>
            <Header
              roomId={roomId}
              like={like}
              showLikeButton={showLikeButton}
              roomUrl={roomUrl}
              onShared={onShared}
            />
          </StickyContainer>
          <ImageList
            images={images}
            roomName={roomName}
            selectedIndex={selectedIndex}
            onImageClick={handleImageClick}
          />
        </div>
        <Lightbox
          index={lightboxIndex}
          onClose={handleLightboxClose}
          images={images}
          roomName={roomName}
        />
      </Sheet.Content>
    </Sheet.Root>
  )
}
